import { CheckCircle2, ImageIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import type { QueuedUpload } from "@/lib/upload-queue";
import { cn } from "@/lib/utils";

/**
 * A photo still on this phone, from its own bytes. The object URL lives as long as the tile.
 * A sent photo's bytes are gone from the phone, so it shows a tick instead.
 */
export function QueuedThumb({ item, className }: { item: QueuedUpload; className?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const hasBytes = item.blob.size > 0;
  // Every queue change re-reads the rows, each with a fresh Blob of the same bytes; keying on
  // the photo's id keeps one URL per photo instead of a new one (and a flicker) per change.
  const blob = useRef(item.blob);
  blob.current = item.blob;

  const photoId = hasBytes ? item.id : null;

  useEffect(() => {
    if (!photoId) {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob.current);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [photoId]);

  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted",
        className,
      )}
    >
      {url ? (
        <img src={url} alt={item.fileName} decoding="async" className="size-full object-cover" />
      ) : item.status === "sent" ? (
        <CheckCircle2 className="size-5 text-[var(--success)]" aria-label="Sent" />
      ) : (
        <ImageIcon className="size-5 text-muted-foreground/60" />
      )}
    </span>
  );
}
